'use client';

import Link from 'next/link';

import { HeBtn } from '@/components/mh/HeBtn';
import { CAPABILITIES } from '../../_lib/capabilities';
import { useHubData } from '../../_lib/use-hub-data';
import { CAPABILITY_ICONS } from '../icons';
import type {
  HunterNote,
  ModeSummary,
  OutcomeRow,
  PriorityItem,
} from '@/lib/hub/types';

type Briefing = { greeting?: string; body?: string; generated_at?: string; empty?: boolean };

const CAP_LABEL: Record<string, string> = {
  respond: 'Respond',
  publish: 'Publish',
  sell: 'Sell',
  attract: 'Attract',
  contacts: 'Contacts',
};

function capVerbLabel(verb: string): string {
  const v = verb.trim();
  return `Review & ${v}`;
}

export function Today() {
  const briefing = useHubData<Briefing>('/api/hub/briefing');
  const mode = useHubData<ModeSummary>('/api/hub/mode-summary');
  const stack = useHubData<PriorityItem[]>('/api/hub/priority-stack');
  const outcomes = useHubData<OutcomeRow[]>('/api/hub/outcomes-feed?since=today');
  const note = useHubData<HunterNote>('/api/hub/hunter-note?surface=today');

  const greeting = briefing.data?.greeting || 'Welcome back.';
  const stackItems = stack.data ?? [];

  return (
    <div className="hub-shell">
      <div className="hub-col-tabbed">
        <div className="hub-hero">
          <h1 className="t">{greeting}</h1>
          <p className="s">
            {stackItems.length > 0
              ? `${stackItems.length} ${stackItems.length === 1 ? 'thing needs' : 'things need'} your OK today.`
              : 'You’re all caught up.'}
          </p>
        </div>

        {/* Mode strip */}
        {mode.data && (mode.data.auto_count + mode.data.suggest_count + mode.data.human_count) > 0 ? (
          <div className="hs-strip">
            <div className="hs-copy">
              This week Hunter handled <em>most of your work on its own</em>. It paused to ask you{' '}
              <strong>{mode.data.paused_for_review} {mode.data.paused_for_review === 1 ? 'time' : 'times'}</strong>.
            </div>
          </div>
        ) : null}

        {/* Hunter Briefing */}
        {briefing.data && !briefing.data.empty && briefing.data.body ? (
          <div className="hb-briefing">
            <div className="hb-briefing-head">
              <span className="hb-briefing-eyebrow">Hunter Briefing</span>
              <span className="hb-briefing-meta">· just now</span>
            </div>
            <p className="hb-briefing-pre">Since yesterday:</p>
            <p className="hb-briefing-quote">{briefing.data.body}</p>
          </div>
        ) : null}

        {/* Priority stack */}
        <div className="h2-eyebrow">
          <span>Waiting for your OK</span>
          {stackItems.length > 0 ? <span className="right">{stackItems.length} things</span> : null}
        </div>
        {stack.isLoading ? (
          <div className="hub-skeleton">Loading…</div>
        ) : stackItems.length === 0 ? (
          <div className="hub-empty">Nothing waiting for you right now.</div>
        ) : (
          <div className="ps-stack">
            {stackItems.map((item) => (
              <div key={item.id} className="ps-row">
                <span className="ps-tag">{CAP_LABEL[item.capability] ?? item.capability}</span>
                <div className="ps-copy">{item.summary}</div>
                <HeBtn
                  label={capVerbLabel(item.action_label)}
                  onPrimary={() => {
                    window.location.href = item.action_url;
                  }}
                  options={[
                    {
                      id: 'review',
                      label: capVerbLabel(item.action_label),
                      onSelect: () => {
                        window.location.href = item.action_url;
                      },
                    },
                    { id: 'later', label: 'Remind me later', onSelect: () => {} },
                    { id: 'no', label: 'No thanks', onSelect: () => {} },
                  ]}
                />
              </div>
            ))}
          </div>
        )}

        {/* Outcomes feed — pure ✓ */}
        {(() => {
          const outcomeRows = outcomes.data ?? [];
          return (
            <>
              <div className="h2-eyebrow">
                <span>What Hunter did today</span>
                {outcomeRows.length > 0 ? (
                  <span className="right">{outcomeRows.length} things</span>
                ) : null}
              </div>
              {outcomes.isLoading ? (
                <div className="hub-skeleton">Loading…</div>
              ) : outcomeRows.length === 0 ? (
                <div className="hub-empty">Quiet day. Hunter is watching.</div>
              ) : (
                <div className="of-feed">
                  {outcomeRows.map((row) => (
                    <div key={row.id} className="of-row">
                      <span className="of-mark">{row.icon}</span>
                      <div className="of-copy">{row.text}</div>
                      <span className="of-when">{row.when}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}

        {/* Capability launchers (static) */}
        <div className="lp-eyebrow">Create</div>
        <div className="lp-grid-5">
          {CAPABILITIES.map((cap) => (
            <Link key={cap.id} href={cap.href} className="lp-card">
              <div className="lp-card-icon">{CAPABILITY_ICONS[cap.id]}</div>
              <div>
                <div className="t">{cap.label}</div>
                <div className="s">{cap.subtext}</div>
              </div>
            </Link>
          ))}
        </div>

        {/* Hunter note footer */}
        {note.data ? (
          <div className="hn-foot">
            {note.data.em
              ? note.data.text.split('{em}').map((part, i, arr) => (
                  <span key={i}>
                    {part}
                    {i < arr.length - 1 ? <em>{note.data!.em}</em> : null}
                  </span>
                ))
              : note.data.text}
          </div>
        ) : null}
      </div>
    </div>
  );
}
